import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase/config";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { collection, onSnapshot, doc, updateDoc } from "firebase/firestore";
import { questions, sections } from "../data/questions";

const LETTERS = ["A", "B", "C", "D"];

export default function AdminPanel() {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markValues, setMarkValues] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [viewModal, setViewModal] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterSection, setFilterSection] = useState("all");

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (!user) { navigate("/admin"); return; }
      setAdmin(user);
    });
    return unsub;
  }, [navigate]);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "submissions"), (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => (b.submittedAt?.seconds || 0) - (a.submittedAt?.seconds || 0));
      setSubmissions(data);
      const mv = {};
      data.forEach(s => { mv[s.id] = s.adminMark !== null && s.adminMark !== undefined ? s.adminMark : ""; });
      setMarkValues(prev => ({ ...mv, ...prev }));
      setLoading(false);
    });
    return unsub;
  }, []);

  const handleSaveMark = async (id) => {
    const val = markValues[id];
    if (val === "" || isNaN(Number(val)) || Number(val) < 0 || Number(val) > 30) { alert("Please enter a valid mark between 0 and 30."); return; }
    setSavingId(id);
    try {
      await updateDoc(doc(db, "submissions", id), { adminMark: Number(val), status: "marked", markedAt: new Date().toISOString() });
    } catch (err) { alert("Error saving mark: " + err.message); }
    setSavingId(null);
  };

  const handleLogout = async () => { await signOut(auth); navigate("/admin"); };

  const formatDate = (ts) => {
    if (!ts?.seconds) return "—";
    return new Date(ts.seconds * 1000).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const getScoreClass = (score, total) => {
    const pct = (score / total) * 100;
    return pct >= 70 ? "high" : pct >= 40 ? "mid" : "low";
  };

  const filtered = submissions.filter(s => {
    const name = s.student?.name?.toLowerCase() || "";
    const email = s.student?.email?.toLowerCase() || "";
    const search = searchTerm.toLowerCase();
    if (search && !name.includes(search) && !email.includes(search)) return false;
    if (filterSection === "marked" && s.status !== "marked") return false;
    if (filterSection === "pending" && s.status !== "pending_review") return false;
    return true;
  });

  const totalSubmissions = submissions.length;
  const markedCount = submissions.filter(s => s.status === "marked").length;
  const pendingCount = submissions.filter(s => s.status === "pending_review").length;
  const avgScore = submissions.length ? Math.round(submissions.reduce((sum, s) => sum + (s.autoScore || 0), 0) / submissions.length) : 0;

  if (!admin) return <div className="loader" />;

  return (
    <div className="page-wrapper">
      <header className="site-header">
        <div className="logo-wrap">
          <img src="https://hyperdigitech.com/assets/logo-De65uxdz.webp" alt="HyperDigiTech" onError={e => { e.target.style.display = "none"; }} />
          <span style={{ fontFamily: "'Poppins',sans-serif", fontWeight: 700, fontSize: "0.9rem", color: "var(--muted)" }}>Admin Panel</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: "0.8rem", color: "var(--muted)" }}>{admin.email}</span>
          <button onClick={handleLogout} style={{ padding: "8px 16px", background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 10, color: "var(--danger)", fontSize: "0.82rem", cursor: "pointer", fontFamily: "'Poppins',sans-serif" }}>Sign Out</button>
        </div>
      </header>

      <div className="admin-wrapper">
        <div style={{ marginBottom: 28 }}>
          <h1 style={{ fontFamily: "'Poppins',sans-serif", fontWeight: 800, fontSize: "1.8rem", marginBottom: 4 }}>Assessment Dashboard</h1>
          <p style={{ color: "var(--muted)", fontSize: "0.88rem" }}>Manage student submissions and assign marks</p>
        </div>

        <div className="admin-stats">
          {[{ val: totalSubmissions, lbl: "Total Submissions" }, { val: markedCount, lbl: "Marked" }, { val: pendingCount, lbl: "Pending Review" }, { val: `${avgScore}/30`, lbl: "Avg Auto Score" }].map(s => (
            <div className="admin-stat-card" key={s.lbl}><div className="val">{s.val}</div><div className="lbl">{s.lbl}</div></div>
          ))}
        </div>

        <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
          <input className="form-input" style={{ maxWidth: 260, padding: "10px 16px" }} type="text" placeholder="Search by name or email..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
          {["all", "pending", "marked"].map(f => (
            <button key={f} onClick={() => setFilterSection(f)} style={{ padding: "10px 18px", borderRadius: 10, border: `1px solid ${filterSection === f ? "var(--accent)" : "var(--border)"}`, background: filterSection === f ? "rgba(0,212,255,0.1)" : "transparent", color: filterSection === f ? "var(--accent)" : "var(--muted)", cursor: "pointer", fontSize: "0.85rem", fontFamily: "'Poppins',sans-serif" }}>
              {f === "all" ? "All" : f === "pending" ? "⏳ Pending" : "✅ Marked"}
            </button>
          ))}
        </div>

        {loading ? <div className="loader" /> : filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 20px", color: "var(--muted)" }}>
            <div style={{ fontSize: "3rem", marginBottom: 12 }}>📭</div><p>No submissions found.</p>
          </div>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>#</th><th>Student Name</th><th>Mobile</th><th>Email</th>
                  <th>Auto Score</th><th>Time Taken</th><th>Submitted At</th>
                  <th>Tab Switch</th><th>Answers</th><th>Admin Mark (/30)</th><th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((sub, idx) => (
                  <tr key={sub.id}>
                    <td style={{ color: "var(--muted)", fontSize: "0.8rem" }}>{idx + 1}</td>
                    <td><strong style={{ fontFamily: "'Poppins',sans-serif", fontSize: "0.9rem" }}>{sub.student?.name || "—"}</strong></td>
                    <td style={{ fontFamily: "monospace", color: "var(--muted)" }}>{sub.student?.mobile || "—"}</td>
                    <td style={{ color: "var(--muted)", fontSize: "0.83rem" }}>{sub.student?.email || "—"}</td>
                    <td><span className={`score-badge ${getScoreClass(sub.autoScore, sub.totalQuestions)}`}>{sub.autoScore}/{sub.totalQuestions}</span></td>
                    <td style={{ color: "var(--muted)", fontSize: "0.8rem" }}>{sub.timeTaken ? `${Math.floor(sub.timeTaken/60)}m ${sub.timeTaken%60}s` : "—"}</td>
                    <td style={{ color: "var(--muted)", fontSize: "0.8rem", whiteSpace: "nowrap" }}>{formatDate(sub.submittedAt)}</td>
                    <td>
                      <span style={{ padding: "3px 8px", borderRadius: 20, fontSize: "0.75rem", fontWeight: 600, background: sub.tabSwitched ? "rgba(239,68,68,0.1)" : "rgba(16,185,129,0.1)", color: sub.tabSwitched ? "var(--danger)" : "var(--success)" }}>
                        {sub.tabSwitched ? "⚠ Yes" : "✓ No"}
                      </span>
                    </td>
                    <td><button className="answers-btn" onClick={() => setViewModal(sub)}>View Answers</button></td>
                    <td>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <input className="mark-input" type="number" min="0" max="30" placeholder="—" value={markValues[sub.id] ?? ""} onChange={e => setMarkValues(prev => ({ ...prev, [sub.id]: e.target.value }))} />
                        <button className="save-mark-btn" onClick={() => handleSaveMark(sub.id)} disabled={savingId === sub.id}>{savingId === sub.id ? "..." : "Save"}</button>
                      </div>
                    </td>
                    <td>
                      <span style={{ padding: "4px 10px", borderRadius: 20, fontSize: "0.75rem", fontWeight: 600, fontFamily: "'Poppins',sans-serif", background: sub.status === "marked" ? "rgba(16,185,129,0.1)" : "rgba(245,158,11,0.1)", color: sub.status === "marked" ? "var(--success)" : "var(--gold)", border: `1px solid ${sub.status === "marked" ? "rgba(16,185,129,0.2)" : "rgba(245,158,11,0.2)"}` }}>
                        {sub.status === "marked" ? "✓ Marked" : "Pending"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {viewModal && (
        <div className="modal-overlay" onClick={() => setViewModal(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
              <div>
                <h3>{viewModal.student?.name} — Answers</h3>
                <p style={{ color: "var(--muted)", fontSize: "0.82rem" }}>
                  Auto Score: {viewModal.autoScore}/{viewModal.totalQuestions}
                  {viewModal.adminMark !== null && viewModal.adminMark !== undefined && ` · Admin Mark: ${viewModal.adminMark}/30`}
                  {viewModal.timeTaken && ` · Time: ${Math.floor(viewModal.timeTaken/60)}m ${viewModal.timeTaken%60}s`}
                </p>
              </div>
              <button onClick={() => setViewModal(null)} style={{ background: "none", border: "none", color: "var(--muted)", fontSize: "1.4rem", cursor: "pointer", lineHeight: 1 }}>×</button>
            </div>
            {sections.map(sec => {
              const secQs = questions.filter(q => q.sectionKey === sec.key);
              return (
                <div key={sec.key} style={{ marginBottom: 24 }}>
                  <div style={{ fontFamily: "'Poppins',sans-serif", fontSize: "0.75rem", letterSpacing: "1.5px", textTransform: "uppercase", color: "var(--accent)", marginBottom: 10, paddingBottom: 6, borderBottom: "1px solid var(--border)" }}>
                    {sec.icon} {sec.label}
                  </div>
                  {secQs.map((q, qi) => {
                    const givenIdx = viewModal.answers?.[q.id];
                    const isCorrect = givenIdx === q.correct;
                    const notAnswered = givenIdx === undefined;
                    return (
                      <div className="answer-row" key={q.id}>
                        <div className="answer-q">Q{qi + 1}: {q.question}</div>
                        <div className={`answer-given ${notAnswered ? "" : isCorrect ? "answer-correct" : "answer-wrong"}`}>
                          {notAnswered
                            ? <span style={{ color: "var(--muted)", fontStyle: "italic" }}>Not answered</span>
                            : <>{LETTERS[givenIdx]}. {q.options[givenIdx]}
                                {isCorrect
                                  ? <span style={{ marginLeft: 8 }}>✓ Correct</span>
                                  : <span style={{ color: "var(--muted)", fontSize: "0.8rem", display: "block", marginTop: 2 }}>Correct: {LETTERS[q.correct]}. {q.options[q.correct]}</span>
                                }
                              </>
                          }
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}